// Müşteri portalı KPI saf mantığı (M16 T19; DR2/DR3/DR4/DR8). Bileşenler ince kalır; burada:
// landing kararı, müşteri görünümü sayaçları (payda yalnız `open` hücreler), hücre durum rozeti
// (onay boyutu — kontrol boyutu portalda YOK), "danışman düzeltti" satırı, açıklama istenen hücre
// listesi. vitest ile kilitli (portal-kpi-ui.test.ts). Kopya KPI_COPY.portal'dan (RK36).

import {
  KPI_TOTAL_KIND_LABELS,
  type ClientEntity,
  type KpiAccessRole,
  type KpiResolvedCell,
  type KpiResolvedGrid,
  type KpiResolvedIndicator,
  type KpiTotalKind,
} from "@mahir/shared";
import { isWriteBlockedIndicator, rowKindOf, type GridRowKind } from "./kpi-grid-breakdown";
import { formatKpiCell, KPI_COPY } from "./kpi-ui";

// ─── Landing (DR2 / RK14) ────────────────────────────────────────────────────

export type ClientLanding = "kpi" | "forms" | "none";

/**
 * Giriş sonrası yönlendirme: form katılımı yok + set erişimi var → KPI; form varsa ClientHome;
 * ikisi de yok → boş durum. Set sorgusu hata verdiyse (`setsStatus === "error"`) form dalına
 * düşülür (fail-open — RK14): form varsa liste, yoksa mevcut boş durum.
 */
export function decideClientLanding(args: {
  projectCount: number;
  setCount: number;
  setsStatus: "ready" | "error";
}): ClientLanding {
  if (args.projectCount > 0) return "forms";
  if (args.setsStatus === "ready" && args.setCount > 0) return "kpi";
  return "none";
}

// ─── Sayaçlar (DR3 — payda yalnız açık kategoriler) ──────────────────────────

export interface ClientFillStatus {
  /** size açık (editable) hücre sayısı — görünür yıllar × açık göstergeler */
  open: number;
  /** açık hücrelerden dolu olanlar (filled / not_measured / pending_xxx hariç boş değil) */
  filled: number;
  awaiting: number;
  verified: number;
  clarification: number;
  /** salt okunur (readonly) gösterge sayısı — 🔒 */
  readonlyIndicators: number;
  editableIndicators: number;
}

function isCategory(ind: KpiResolvedIndicator, all: KpiResolvedIndicator[]): boolean {
  return all.some((i) => i.parentId === ind.id);
}

/**
 * Kırılım (spec §6.6): satır türü shared `effectiveNodeKind` üstünden (`rowKindOf`) — kategori ve
 * grup bandı değer almaz; kırılımlı toplam kategori DEĞİLDİR (hesap gruplu toplam yazılamaz, ham
 * toplam sayılır). Kırılımsız sette "çocuğu olan = kategori" kuralıyla birebir.
 */
function kindIn(ind: KpiResolvedIndicator, all: KpiResolvedIndicator[]): GridRowKind {
  return rowKindOf(
    ind,
    all.filter((i) => i.parentId === ind.id),
  );
}

/**
 * `entityId` verilirse yalnız o işletmenin hücreleri sayılır (çok işletmeli portal — seçili
 * işletme görünümü; konsolide/grup anahtarları paydaya girmez).
 */
export function countClientFillStatus(grid: KpiResolvedGrid, entityId?: string): ClientFillStatus {
  const s: ClientFillStatus = {
    open: 0,
    filled: 0,
    awaiting: 0,
    verified: 0,
    clarification: 0,
    readonlyIndicators: 0,
    editableIndicators: 0,
  };
  for (const ind of grid.indicators) {
    const kind = kindIn(ind, grid.indicators);
    if (kind === "band" || kind === "group") continue;
    if (ind.isDerived || isWriteBlockedIndicator(ind, grid.indicators)) continue;
    if (ind.effectiveClientAccess === "readonly") s.readonlyIndicators += 1;
    else if (ind.effectiveClientAccess === "open") s.editableIndicators += 1;
    for (const [eid, byYear] of Object.entries(ind.cells)) {
      if (entityId && eid !== entityId) continue;
      for (const year of grid.years) {
        const cell = byYear[year];
        if (cell?.editable !== true) continue;
        s.open += 1;
        if (cell.status === "filled" || cell.status === "not_measured") s.filled += 1;
        if (cell.workflowState === "submitted" || cell.workflowState === "client_approved")
          s.awaiting += 1; // TODO-KPI-10: şirket onayı danışman onayını beklemeye devam eder
        else if (cell.workflowState === "clarification") s.clarification += 1;
        else if (
          cell.workflowState === "verified" &&
          cell.status === "filled" &&
          cell.updatedByKind !== "internal"
        )
          s.verified += 1; // danışman girdisi "onaylandı" sayılmaz (rozet de "danışman girdi")
      }
    }
  }
  return s;
}

/**
 * Kategori (kök bant) düzeyinde düzenlenebilir / yalnız görüntüleme sayısı (footer). Bant
 * işaretiyle aynı kural: kökün kendi etkin erişimi `open` ise düzenlenebilir (gösterge düzeyinde
 * istisnalar — K12 — bandı değiştirmez; satırda 🔒 yokluğuyla görünür). M17: denetçide hiçbir
 * kategori düzenlenebilir değildir (set düzeyinde rol — açıklar da "yalnız görüntüleme" sayılır).
 */
export function countClientCategories(
  grid: KpiResolvedGrid,
  role: KpiAccessRole = "owner",
): {
  editable: number;
  readonly: number;
} {
  let editable = 0;
  let readonly = 0;
  for (const ind of grid.indicators) {
    if (ind.parentId !== null || !isCategory(ind, grid.indicators)) continue;
    if (kindIn(ind, grid.indicators) !== "band") continue; // kökteki kırılım toplamı / taşınmış grup
    if (ind.structural) continue; // Y4a — kapalı kategorinin iskeleti "yalnız görüntüleme" sayılmaz
    if (ind.effectiveClientAccess === "open" && role !== "auditor") editable += 1;
    else readonly += 1;
  }
  return { editable, readonly };
}

/** Veri Girişi doluluk şeridi metni — denetçide "N boş" daveti yok (M17). */
export function portalFillCounterText(
  fill: { open: number; filled: number; awaiting: number; clarification: number },
  role: KpiAccessRole = "owner",
): string {
  return role === "auditor" ? KPI_COPY.portal.counterAuditor(fill) : KPI_COPY.portal.counter(fill);
}

// ─── Durum rozeti (DR4 — onay boyutu) ────────────────────────────────────────

export type ClientStatusTone = "await" | "verified" | "clarify" | "muted" | "dot";

export interface ClientStatusBadge {
  text: string;
  tone: ClientStatusTone;
  /** pill (rozet) mi düz metin mi */
  pill: boolean;
}

/**
 * Hücre durumu (portal "yıl durumu" kolonu): müşteri girdilerinde onay rozeti; danışman girdisinde
 * rozet YOK ("danışman girdi"); readonly satırda "danışman girer"; türetilmişte "hesaplanır"
 * (girdisi onay bekliyorsa turuncu nokta); boşsa "boş".
 */
export function clientStatusBadge(
  ind: Pick<KpiResolvedIndicator, "isDerived" | "effectiveClientAccess">,
  cell: KpiResolvedCell | undefined,
): ClientStatusBadge {
  const S = KPI_COPY.portal.status;
  if (ind.isDerived) {
    if (cell?.unverified) return { text: S.inputPending, tone: "dot", pill: false };
    return { text: S.computed, tone: "muted", pill: false };
  }
  if (ind.effectiveClientAccess === "readonly") {
    return { text: S.consultantEnters, tone: "muted", pill: false };
  }
  if (!cell || cell.status === "empty") return { text: S.empty, tone: "muted", pill: false };
  switch (cell.workflowState) {
    case "submitted":
      return { text: KPI_COPY.workflow.submitted, tone: "await", pill: true };
    case "client_approved":
      // TODO-KPI-10 — şirket onayı; danışman onayı hâlâ bekleniyor (aynı "await" tonu)
      return { text: KPI_COPY.workflow.client_approved, tone: "await", pill: true };
    case "clarification":
      return { text: KPI_COPY.workflow.clarification, tone: "clarify", pill: true };
    case "verified":
      if (cell.updatedByKind === "internal")
        return { text: S.consultantEntered, tone: "muted", pill: false };
      return { text: KPI_COPY.workflow.verified, tone: "verified", pill: true };
    default:
      return { text: S.empty, tone: "muted", pill: false };
  }
}

// ─── Danışman düzeltmesi (DR8) ───────────────────────────────────────────────

/**
 * "danışman düzeltti 9 → 11 · 22.09" — yalnız danışmanın (internal) yazdığı, daha önce müşteri
 * girdisi olmuş hücrede. M17: sinyal sunucunun `correctedAt`'i (müşterinin son değer yazımından
 * sonraki danışman DEĞER yazımı — kanıt bağı / taşıma sürümleri sayılmaz); tarih de odur. Önceki
 * müşteri değeri `previousClientValue` / `previousClientText`; yoksa kısa form "danışman düzeltti · tarih".
 */
export function correctionLine(
  cell: KpiResolvedCell | undefined,
  opts: { percent?: boolean } = {},
): string | null {
  if (cell?.updatedByKind !== "internal" || !cell.correctedAt) return null;
  const when = new Date(cell.correctedAt).toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
  });
  const to = formatKpiCell(cell, opts).text;
  const from =
    typeof cell.previousClientValue === "number"
      ? formatKpiCell({ status: "filled", isDerived: false, value: cell.previousClientValue }, opts)
          .text
      : typeof cell.previousClientText === "string" && cell.previousClientText
        ? cell.previousClientText
        : null;
  return KPI_COPY.portal.corrected(from, to, when);
}

// ─── Satır durum özeti (Finar 10.5 — sağ sütun) ──────────────────────────────

export interface StatusCounts {
  awaiting: number;
  corrected: number;
  clarification: number;
}

/** Bir satırın görünür yıl hücrelerindeki bekleyen / düzeltilen / açıklama istenen sayıları. */
export function rowStatusCounts(
  ind: Pick<KpiResolvedIndicator, "isDerived" | "effectiveClientAccess" | "valueType">,
  cells: (KpiResolvedCell | undefined)[],
): StatusCounts {
  const out: StatusCounts = { awaiting: 0, corrected: 0, clarification: 0 };
  for (const cell of cells) {
    const b = clientStatusBadge(ind, cell);
    if (b.tone === "await") out.awaiting += 1;
    if (b.tone === "clarify") out.clarification += 1;
    if (correctionLine(cell, { percent: ind.valueType === "percent" })) out.corrected += 1;
  }
  return out;
}

/** "1 danışman onayında · 1 düzeltildi" — sıfırlar atlanır; hepsi sıfırsa boş metin. */
export function statusSummary(c: StatusCounts): string {
  const S = KPI_COPY.portal.summary;
  const parts: string[] = [];
  if (c.awaiting > 0) parts.push(S.awaiting(c.awaiting));
  if (c.clarification > 0) parts.push(S.clarification(c.clarification));
  if (c.corrected > 0) parts.push(S.corrected(c.corrected));
  return parts.join(" · ");
}

// ─── Açıklama istenen hücreler (DR6 bandı) ───────────────────────────────────

export interface ClarificationItem {
  indicatorId: string;
  code: string;
  label: string;
  entityId: string;
  year: number;
  note: string | null;
}

export function collectClarifications(grid: KpiResolvedGrid): ClarificationItem[] {
  const out: ClarificationItem[] = [];
  for (const ind of grid.indicators) {
    for (const [entityId, byYear] of Object.entries(ind.cells)) {
      for (const [y, cell] of Object.entries(byYear)) {
        if (cell.workflowState !== "clarification") continue;
        out.push({
          indicatorId: ind.id,
          code: ind.code,
          label: ind.label,
          entityId,
          year: Number(y),
          note: cell.clarificationNote ?? null,
        });
      }
    }
  }
  return out.sort((a, b) => b.year - a.year || a.label.localeCompare(b.label, "tr"));
}

/** Özet "Onay durumu" paneli: en son onaylanan müşteri girdisinin tarihi (yoksa null). */
export function lastVerifiedAt(grid: KpiResolvedGrid): string | null {
  let best: string | null = null;
  for (const ind of grid.indicators) {
    for (const byYear of Object.values(ind.cells)) {
      for (const cell of Object.values(byYear)) {
        if (cell.workflowState !== "verified" || cell.updatedByKind !== "client") continue;
        if (cell.updatedAt && (!best || cell.updatedAt > best)) best = cell.updatedAt;
      }
    }
  }
  return best;
}

/** Portal hata kodu → kullanıcı metni (import/yazma). */
export function portalErrorMessage(code: string, fallback: string): string {
  const C = KPI_COPY.portal.import;
  if (code === "KPI_CLIENT_FORBIDDEN_ACTION") return C.forbiddenAction;
  if (code === "KPI_CLIENT_FORBIDDEN") return C.forbidden;
  // kırılım toplamı / grup satırı yazımı (spec §6.2) — tek genel "alt kalem" kopyası
  if (code === "KPI_DERIVED_READONLY" || code === "KPI_GROUP_READONLY")
    return KPI_COPY.portal.breakdown.autoComputed;
  return fallback;
}

// ─── Çok işletmeli portal (P1-1) ─────────────────────────────────────────────

/** Konsolide (salt okunur) görünüm için seçici değeri — gerçek işletme id'si değil. */
export const PORTAL_CONSOLIDATED = "consolidated";

/** Aktif yaprak işletmeler (grubu olmayan ya da aktif çocuğu olmayan), orderIndex sırasıyla. */
export function portalLeafEntities(entities: ClientEntity[]): ClientEntity[] {
  return entities
    .filter((e) => e.isActive && !entities.some((c) => c.parentId === e.id && c.isActive))
    .sort((a, b) => a.orderIndex - b.orderIndex);
}

export interface PortalEntityChoice {
  /** seçici gösterilsin mi — birden çok aktif yaprak işletme varsa */
  show: boolean;
  leaves: ClientEntity[];
  /** varsayılan seçim: `isDefault` yaprak, yoksa ilk yaprak; tek yaprakta o yaprak */
  defaultId: string | null;
}

/**
 * Danışman işletme/grup tanımlayınca portal tablosu konsolide (salt okunur) görünüme düşmesin:
 * birden çok yaprak varsa müşteri bir işletme seçer, grid o işletmeye daraltılır (scopeGridToEntity).
 * `stored` (localStorage) geçerli bir yaprak ya da konsolide ise korunur.
 */
export function decidePortalEntity(
  entities: ClientEntity[],
  stored: string | null,
): PortalEntityChoice & { selected: string | null } {
  const leaves = portalLeafEntities(entities);
  const show = leaves.length > 1;
  const defaultId = (leaves.find((l) => l.isDefault) ?? leaves[0])?.id ?? null;
  if (!show) return { show, leaves, defaultId, selected: defaultId };
  const valid =
    stored === PORTAL_CONSOLIDATED || (stored !== null && leaves.some((l) => l.id === stored));
  return { show, leaves, defaultId, selected: valid ? stored : defaultId };
}

/**
 * §12/1 — seçili işletme görünümünde toplam sütun grubu için: `scopeGridToEntity` çıktısına sunucunun
 * TAM işletme listesi, `entityScope` ve gerçek toplam (`consolidated`) geri konur. Yıl kolonları yine
 * seçili işletmenin hücrelerinden okunur (`cells` daraltılmış kalır) — yalnız toplam sütunları
 * `consolidated`'a bakar. Özet gibi "konsolide = seçili işletme" okuyan yüzeyler daraltılmış grid'i
 * kullanmaya devam eder.
 */
export function withTotals(scoped: KpiResolvedGrid, full: KpiResolvedGrid): KpiResolvedGrid {
  const byId = new Map(full.indicators.map((i) => [i.id, i]));
  return {
    ...scoped,
    entities: full.entities,
    ...(full.entityScope ? { entityScope: full.entityScope } : {}),
    indicators: scoped.indicators.map((ind) => {
      const { consolidated: _c, ...rest } = ind;
      void _c;
      const total = byId.get(ind.id)?.consolidated;
      return total ? { ...rest, consolidated: total } : rest;
    }),
  };
}

/** Toplam sütunu/seçeneği türü: kapsamlı çözümde `entityScope`, yoksa set özeti, yoksa konsolide. */
export function portalTotalKind(
  grid: Pick<KpiResolvedGrid, "entityScope"> | null,
  set: { totalKind: KpiTotalKind } | undefined,
): KpiTotalKind {
  return grid?.entityScope?.totalKind ?? set?.totalKind ?? "consolidated";
}

/** İşletme seçicideki toplam seçeneğinin etiketi — kapsamlıya "Konsolide" yazılmaz (Y6). */
export function portalTotalOptionLabel(kind: KpiTotalKind): string {
  if (kind === "scope") return `${KPI_TOTAL_KIND_LABELS.scope} (yalnız görüntüleme)`;
  return KPI_COPY.portal.entityConsolidated;
}

/** Toplam görünümünde bilgi bandı metni (tam kapsam: konsolide; kapsamlı: kapsam toplamı). */
export function portalTotalBand(kind: KpiTotalKind): string {
  return kind === "scope" ? KPI_COPY.scopeTotalReadonlyHint : KPI_COPY.portal.consolidatedBand;
}

/**
 * Grid'i tek işletmeye daraltır: `entities` yalnız o yaprak (buildColumns "Yıllar" görünümünde
 * aggregate=false → hücreler düzenlenebilir), `consolidated` = o işletmenin hücreleri (Özet stat
 * kartları/karşılaştırma tablosu aynı veriyi okur), ara toplamlar düşer. Sunucu `entityIds=`
 * filtresi gruplari da taşıdığı için yaprak sayımı FE'de kesinleştirilir.
 */
export function scopeGridToEntity(grid: KpiResolvedGrid, entityId: string): KpiResolvedGrid {
  const leaf = grid.entities.find((e) => e.id === entityId);
  return {
    ...grid,
    entities: leaf ? [leaf] : grid.entities.filter((e) => e.id === entityId),
    geometry: { entityTable: false, consolidatedYears: true },
    indicators: grid.indicators.map((ind) => {
      const { subtotals: _s, ...rest } = ind;
      void _s;
      const own = ind.cells[entityId];
      return {
        ...rest,
        cells: own ? { [entityId]: own } : {},
        ...(own ? { consolidated: own } : {}),
      };
    }),
  };
}

// ─── Formül popover (P3-18 — ham kod yerine etiket) ──────────────────────────

/** `enerji_tuketimi_kalemleri` → "Enerji tuketimi kalemleri" (etiket bilinmeyen kapalı girdiler). */
export function humanizeCode(code: string): string {
  const t = code.replace(/_+/g, " ").trim();
  return t ? t.charAt(0).toLocaleUpperCase("tr") + t.slice(1) : code;
}

export interface ExpressionToken {
  text: string;
  code?: string;
  label?: string;
}

/**
 * Formül ifadesini parçalara ayırır; tanımlayıcılar `labelByCode` ile etikete çevrilir (kod
 * `title`'a gider). Sayılar/işleçler olduğu gibi. Etiketi bilinmeyen kod humanize edilir.
 */
export function tokenizeExpression(
  expression: string,
  labelByCode: Record<string, string>,
): ExpressionToken[] {
  const out: ExpressionToken[] = [];
  // Çift tırnaklı dize (CEVIR birimi, TOPLA_ALT("cinsiyet") boyutu) gösterge kodu değildir
  const re = /"[^"]*"|[a-z][a-z0-9_]*/g;
  let last = 0;
  for (const m of expression.matchAll(re)) {
    if (m[0].startsWith('"')) continue;
    const idx = m.index ?? 0;
    if (idx > last) out.push({ text: expression.slice(last, idx) });
    const code = m[0];
    const label = labelByCode[code] ?? humanizeCode(code);
    out.push({ text: label, code, label });
    last = idx + code.length;
  }
  if (last < expression.length) out.push({ text: expression.slice(last) });
  return out;
}

// ─── Hücre geçmişi (P3-16 — portal etiketleri) ───────────────────────────────

/**
 * Portal Geçmiş sekmesi: iç not satırı hiç görünmez (sunucu zaten düşürür — çift kilit); açıklama
 * isteği satırı müşteriye "Danışmanınızın açıklama isteği" olarak etiketlenir. Dalga 3: müşteri notu
 * artık hücre DTO'sunda değil, not iş parçacığında (`KpiCellNotes`) — burada okuyucusu yok.
 */
export function portalHistoryRows(
  rows: { label: string; value: string }[],
  labels: { note: string; clarificationNote: string },
): { label: string; value: string }[] {
  const H = KPI_COPY.portal.history;
  return rows
    .filter((r) => r.label !== labels.note)
    .map((r) => (r.label === labels.clarificationNote ? { ...r, label: H.clarificationNote } : r));
}
